import { ChangeDetectorRef, Component, NgZone, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, ParamMap } from '@angular/router';
import { Subscription } from 'rxjs';
import {
  EMBED_CHILD_TO_PARENT_TYPE,
  EMBED_POSTMESSAGE_TYPE,
  ExternalAssistantEmbedOptions,
  parseEmbedOptionsFromSearchParams
} from 'src/embed-shared/external-assistant-embed-options';
import {
  EmbedChildToParentMessageAction,
  EmbedPanelMessageAction
} from 'src/embed-shared/external-assistant-embed.enums';
import { ExternalAssistantAuthService, ExternalAssistantTokenResponse } from 'src/app/services/ai/external-assistant-auth.service';
import { CommonService } from 'src/app/services/common/common.service';

/** Payload from the parent embed SDK (trusted via nonce + source checks). */
interface XaAiEmbedPostMessage {
  type: typeof EMBED_POSTMESSAGE_TYPE;
  action: EmbedPanelMessageAction;
  nonce: string;
}

/**
 * Builds {@link URLSearchParams} from an Angular {@link ParamMap} for embed option parsing.
 *
 * @param query Route query param map
 */
function searchParamsFromQueryParamMap(query: ParamMap): URLSearchParams {
  const sp = new URLSearchParams();
  query.keys.forEach((key) => {
    const v = query.get(key);
    if (v !== null) {
      sp.set(key, v);
    }
  });
  return sp;
}

/** Default launcher/header avatar when none is supplied (root-relative so deep embed routes resolve correctly). */
const STATIC_DEFAULT_EXTERNAL_ASSISTANT_AVATAR = '/assets/img/ai-chat-bot-new.png';

/**
 * Normalizes avatar `src` so app-relative paths resolve from the origin root, not the current route segment.
 *
 * @param href Value from token metadata, query, or empty for default
 * @returns Safe `src` string for `<img>` and parent postMessage
 */
function normalizeAvatarSrc(href: string | undefined | null): string {
  const raw = (href ?? '').trim();
  if (!raw) {
    return STATIC_DEFAULT_EXTERNAL_ASSISTANT_AVATAR;
  }
  if (/^(https?:)?\/\//i.test(raw) || raw.startsWith('data:') || raw.startsWith('blob:')) {
    return raw;
  }
  if (raw.startsWith('/')) {
    return raw;
  }
  return `/${raw.replace(/^\/+/, '')}`;
}

@Component({
    selector: 'xa-ai-agent-external-assistant',
    templateUrl: './ai-agent-external-assistant.component.html',
    styleUrls: ['./ai-agent-external-assistant.component.scss'],
    standalone: false
})
export class AiAgentExternalAssistantComponent implements OnInit, OnDestroy {
  public aiApiLoaded = false;
  /** When true, tenant token resolved and {@link agentAssociationId} is set */
  public assistantDataReady = false;
  public agentAssociationId = '';
  public assistantName = '';
  public avatarUrl = STATIC_DEFAULT_EXTERNAL_ASSISTANT_AVATAR;
  public embedOptions: ExternalAssistantEmbedOptions = parseEmbedOptionsFromSearchParams(new URLSearchParams());
  /** Panel visibility (launcher, SDK postMessage, or defaultOpen) */
  public panelOpen = true;

  private routeParamSub?: Subscription;
  private routeQuerySub?: Subscription;
  private readonly onParentEmbedMessageBound = (event: MessageEvent): void => {
    this.onParentEmbedMessage(event);
  };

  /**
   * @param commonService Global loading indicator
   * @param route Current route (tenant path + embed query)
   * @param externalAssistantAuthService Tenant token exchange
   * @param ngZone Runs message handler inside Angular zone for change detection
   * @param cdr Marks view checked after async message handling
   */
  constructor(
    private readonly commonService: CommonService,
    private readonly route: ActivatedRoute,
    private readonly externalAssistantAuthService: ExternalAssistantAuthService,
    private readonly ngZone: NgZone,
    private readonly cdr: ChangeDetectorRef
  ) {}

  /**
   * Subscribes to route params and query, fetches external token, and registers parent postMessage listener.
   */
  ngOnInit(): void {
    window.addEventListener('message', this.onParentEmbedMessageBound);

    this.routeQuerySub = this.route.queryParamMap.subscribe((query) => {
      this.embedOptions = parseEmbedOptionsFromSearchParams(searchParamsFromQueryParamMap(query));
      this.panelOpen = this.embedOptions.defaultOpen;
      this.cdr.markForCheck();
    });

    // The token is fetched by ExternalAssistantGuard; it redirects to the access denied page when the token API fails.
    this.routeParamSub = this.route.paramMap.subscribe((params) => {
      const tenantCode = params.get('tenantCode') || '';
      const tokenResponse = tenantCode ? this.externalAssistantAuthService.getResolvedTokenResponse(tenantCode) : null;
      if (!tokenResponse) {
        this.setAssistantUnavailable();
        return;
      }
      this.handleExternalTokenResponse(tokenResponse);
    });
  }

  /**
   * Removes listeners and route subscriptions.
   */
  ngOnDestroy(): void {
    window.removeEventListener('message', this.onParentEmbedMessageBound);
    this.routeParamSub?.unsubscribe();
    this.routeQuerySub?.unsubscribe();
  }

  /**
   * True when the host SDK iframe (or inline embed) already defines outer size; panel fills it.
   *
   * @returns Whether {@link panelWidthStyle} / {@link panelHeightStyle} use 100%
   */
  panelFillsHostFrame(): boolean {
    return !this.embedOptions.panelFillViewport;
  }

  /**
   * Inline width for `.embed-panel`: unset for viewport fill, else 100% of host iframe.
   *
   * @returns CSS length or null when panel fills viewport via class
   */
  panelWidthStyle(): string | null {
    if (this.embedOptions.panelFillViewport) {
      return null;
    }
    return '100%';
  }

  /**
   * Inline height for `.embed-panel`: unset for viewport fill, else 100% of host iframe.
   *
   * @returns CSS length or null when panel fills viewport via class
   */
  panelHeightStyle(): string | null {
    if (this.embedOptions.panelFillViewport) {
      return null;
    }
    return '100%';
  }

  /**
   * Collapses the panel when the inner assistant header close control emits.
   */
  onAssistantHeaderClosed(): void {
    this.panelOpen = false;
    this.postChildToParentIfEmbedded({ action: EmbedChildToParentMessageAction.PanelClosed });
    this.cdr.markForCheck();
  }

  /**
   * @returns True when the inner assistant should hide its header close icon
   */
  get hideAssistantCloseButton(): boolean {
    return !this.embedOptions.launcher;
  }

  /**
   * Handles trusted embed control messages from the parent window (SDK).
   *
   * @param event Window message event
   */
  private onParentEmbedMessage(event: MessageEvent): void {
    if (event.source !== window.parent) {
      return;
    }
    const data = event.data as Partial<XaAiEmbedPostMessage> | null;
    if (!data || data.type !== EMBED_POSTMESSAGE_TYPE) {
      return;
    }
    const nonce = this.embedOptions.embedNonce;
    if (!nonce || data.nonce !== nonce) {
      return;
    }
    const action = data.action as EmbedPanelMessageAction | undefined;
    if (
      action !== EmbedPanelMessageAction.Open &&
      action !== EmbedPanelMessageAction.Close &&
      action !== EmbedPanelMessageAction.Toggle
    ) {
      return;
    }
    this.ngZone.run(() => {
      if (action === EmbedPanelMessageAction.Open) {
        this.panelOpen = true;
      } else if (action === EmbedPanelMessageAction.Close) {
        this.panelOpen = false;
      } else {
        this.panelOpen = !this.panelOpen;
      }
      this.cdr.markForCheck();
    });
  }

  /**
   * Marks assistant ready after a successful token response and shows the panel per {@link embedOptions.defaultOpen}.
   *
   * @param tokenResponse Token payload from external assistant auth API
   */
  private handleExternalTokenResponse(tokenResponse: ExternalAssistantTokenResponse): void {
    const accessToken = tokenResponse?.accessToken || '';
    const referenceGuid = tokenResponse?.tokenMetaData?.referenceGuid || '';
    if (!accessToken || !referenceGuid) {
      this.setAssistantUnavailable();
      return;
    }
    this.agentAssociationId = referenceGuid;
    this.assistantName = tokenResponse?.tokenMetaData?.name || '';
    const tokenAvatar = tokenResponse?.tokenMetaData?.avatarUrl?.trim();
    this.avatarUrl = normalizeAvatarSrc(tokenAvatar || this.embedOptions.bubble.iconUrl || '');
    this.assistantDataReady = true;
    this.aiApiLoaded = true;
    this.commonService.hideLoading();
    this.cdr.markForCheck();
    if (this.embedOptions.externalLauncher) {
      this.postChildToParentIfEmbedded({
        action: EmbedChildToParentMessageAction.AssistantReady,
        avatarUrl: this.resolveAssetUrlForParent(this.avatarUrl),
        assistantName: this.assistantName
      });
    }
  }

  /**
   * Notifies the parent embed host (when {@link ExternalAssistantEmbedOptions.externalLauncher} is true).
   *
   * @param payload Child-to-parent message body (type and nonce added automatically)
   */
  private postChildToParentIfEmbedded(payload: {
    action: EmbedChildToParentMessageAction;
    avatarUrl?: string;
    assistantName?: string;
  }): void {
    const origin = this.embedOptions.embedParentOrigin;
    const nonce = this.embedOptions.embedNonce;
    if (!this.embedOptions.externalLauncher || !origin || !nonce) {
      return;
    }
    window.parent.postMessage(
      {
        type: EMBED_CHILD_TO_PARENT_TYPE,
        nonce,
        ...payload
      },
      origin
    );
  }

  /**
   * Resolves a possibly app-relative asset URL to an absolute URL for the parent document.
   *
   * @param href Avatar or asset path from token metadata
   */
  private resolveAssetUrlForParent(href: string): string {
    try {
      return new URL(href, window.location.href).href;
    } catch {
      return href;
    }
  }

  /**
   * Clears assistant state when tenant or token is invalid.
   */
  private setAssistantUnavailable(): void {
    this.assistantDataReady = false;
    this.agentAssociationId = '';
    this.aiApiLoaded = true;
    this.commonService.hideLoading();
    this.cdr.markForCheck();
  }
}
